import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { fmtCents, fmtDate } from "./util";
import { StatusPill } from "@/components/primitives";
import { PayoutAllocation } from "./viz";
import { X, Check, Planet, Clock, Plus, ClockCounterClockwise, CaretDown, Warning } from "@phosphor-icons/react";

const DURATIONS = [2, 4, 6, 8, 10, 12];
const inputCls = "w-full rounded-lg border border-white/10 bg-[#08090F] px-3.5 py-2.5 text-sm text-white focus:border-[#2DD4BF]/50 focus:outline-none";

function PerformanceTerms() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0B10] overflow-hidden" data-testid="qv-perf-terms">
      <button onClick={() => setOpen(!open)} data-testid="qv-terms-toggle" className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left">
        <span className="flex items-center gap-2.5 text-sm font-semibold text-white"><Warning size={18} weight="duotone" className="text-amber-400" /> Full performance &amp; risk terms — how returns actually work</span>
        <CaretDown size={18} className={`shrink-0 text-[#64748B] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 text-sm text-[#94A3B8] leading-relaxed border-t border-white/5 pt-4" data-testid="qv-terms-body">
          <p><span className="text-white font-semibold">Real, variable performance.</span> Q-Core is an automated trading engine. Daily results vary and are settled to your wallets from the immutable ledger — they are <span className="text-white">not</span> a fixed or guaranteed rate.</p>
          <p><span className="text-white font-semibold">±3% daily band, weekdays only.</span> The engine targets a realistic range of roughly ±3% per <span className="text-white">trading day</span> (Monday–Friday). There is no trading on weekends.</p>
          <p><span className="text-white font-semibold">Upside is capped by your plan.</span> Each configuration caps the maximum daily <span className="text-emerald-300">gain</span> (shown as the daily upside cap). Days above the cap are clipped to the cap.</p>
          <p><span className="text-red-300 font-semibold">Downside is NOT capped.</span> Loss days are real and are posted to your ledger. A trading day can be as low as −3%. Losses reduce your portfolio value and wallet balances.</p>
          <p><span className="text-white font-semibold">Circuit breaker protection.</span> If the engine records 5 consecutive maximum-loss (−3%) trading days, it halts the portfolio and refunds 85% of remaining capital to your Main wallet.</p>
          <p className="text-xs text-[#64748B] font-mono">Automated trading involves substantial risk, including possible loss of capital. Past performance does not guarantee future results. Nothing here is financial advice.</p>
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0A0B10] p-6" onClick={(e) => e.stopPropagation()} data-testid="qv-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-white text-lg">{title}</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PurchaseFlow({ config, onClose, onDone }) {
  const [amount, setAmount] = useState((config.min_cents / 100).toString());
  const [payout, setPayout] = useState("daily");
  const [duration, setDuration] = useState(6);
  const [funding, setFunding] = useState("direct");
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const amountCents = Math.round(parseFloat(amount || "0") * 100);
  const dailyCents = Math.floor((amountCents * config.daily_rate_bps) / 10000);
  const start = new Date();
  const end = new Date(Date.now() + 360 * 86400000);

  const submit = async () => {
    if (!ack1 || !ack2) { toast.error("Please acknowledge the portfolio terms and risk."); return; }
    setBusy(true);
    try {
      await api.post("/portfolios", {
        config_id: config.id, amount: parseFloat(amount), currency: "USD",
        payout_type: payout, duration_months: payout === "daily" ? 12 : duration, funding_source: funding,
      });
      toast.success("Portfolio created. Payouts will begin accruing.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Purchase failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal title={confirm ? "Confirm Purchase" : `Purchase — ${config.name}`} onClose={onClose}>
      {!confirm ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#cbd5e1] mb-1.5">Amount (USD)</label>
            <input data-testid="qv-amount" type="number" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} />
            <p className="text-xs text-[#64748B] mt-1">Min {fmtCents(config.min_cents)} · Max {fmtCents(config.max_cents)}</p>
          </div>
          <div>
            <label className="block text-sm text-[#cbd5e1] mb-1.5">Payout Type</label>
            <div className="grid grid-cols-3 gap-2">
              {["daily", "staking", "hybrid"].map((pt) => (
                <button key={pt} onClick={() => setPayout(pt)} data-testid={`qv-payout-${pt}`} className={`px-3 py-2 rounded-lg text-sm capitalize border ${payout === pt ? "border-[#2DD4BF]/50 bg-[#2DD4BF]/10 text-[#2DD4BF]" : "border-white/10 text-[#94A3B8]"}`}>{pt}</button>
              ))}
            </div>
            <div className="mt-3">
              <PayoutAllocation type={payout} />
            </div>
          </div>
          {payout !== "daily" && (
            <div>
              <label className="block text-sm text-[#cbd5e1] mb-1.5">Allocation Duration (months)</label>
              <div className="grid grid-cols-6 gap-2">
                {DURATIONS.map((d) => (
                  <button key={d} onClick={() => setDuration(d)} className={`py-2 rounded-lg text-sm border ${duration === d ? "border-[#2DD4BF]/50 bg-[#2DD4BF]/10 text-[#2DD4BF]" : "border-white/10 text-[#94A3B8]"}`}>{d}</button>
                ))}
              </div>
              <p className="text-xs text-[#64748B] mt-1.5">After {duration} months, allocation reverts to 100% Main (12-month total term).</p>
            </div>
          )}
          <div>
            <label className="block text-sm text-[#cbd5e1] mb-1.5">Funding Source</label>
            <select data-testid="qv-funding" className={inputCls} value={funding} onChange={(e) => setFunding(e.target.value)}>
              <option value="direct" className="bg-[#08090F]">Direct Deposit</option>
              <option value="main" className="bg-[#08090F]">Main Wallet</option>
              <option value="staking" className="bg-[#08090F]">Staking Wallet</option>
              <option value="referral" className="bg-[#08090F]">Referral Wallet</option>
            </select>
          </div>
          <button onClick={() => setConfirm(true)} data-testid="qv-review" className="w-full px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors">Review Purchase</button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-white/10 bg-black/20 divide-y divide-white/5 text-sm">
            {[
              ["Portfolio", config.name], ["Amount", fmtCents(amountCents)], ["Currency", "USD"],
              ["Payout Type", payout], ...(payout !== "daily" ? [["Allocation Duration", `${duration} months`]] : []),
              ["Start Date", fmtDate(start)], ["End Date", fmtDate(end)],
              ["Funding Source", funding === "direct" ? "Direct Deposit" : `${funding} wallet`],
              ["Max Daily (capped)", `Up to ${fmtCents(dailyCents)}`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between px-4 py-2.5"><span className="text-[#94A3B8]">{k}</span><span className="text-white capitalize">{v}</span></div>
            ))}
          </div>
          <p className="text-xs text-amber-300/80 rounded-lg border border-amber-400/20 bg-amber-400/[0.05] p-3">
            The daily figure is the <span className="font-semibold">capped upside</span>, not a guarantee. Real Q-Core results vary within a ±3% band on trading days and <span className="text-red-300 font-semibold">can be negative — losses are not capped</span>.
          </p>
          <label className="flex items-start gap-2.5 text-sm text-[#cbd5e1]"><input type="checkbox" data-testid="qv-ack1" checked={ack1} onChange={(e) => setAck1(e.target.checked)} className="mt-0.5 accent-[#2DD4BF] h-4 w-4" /> I understand the portfolio terms.</label>
          <label className="flex items-start gap-2.5 text-sm text-[#cbd5e1]"><input type="checkbox" data-testid="qv-ack2" checked={ack2} onChange={(e) => setAck2(e.target.checked)} className="mt-0.5 accent-[#2DD4BF] h-4 w-4" /> I understand that trading involves risk.</label>
          <div className="flex gap-3">
            <button onClick={() => setConfirm(false)} className="flex-1 px-6 py-3 rounded-full border border-white/15 text-white">Back</button>
            <button onClick={submit} disabled={busy} data-testid="qv-confirm" className="flex-1 px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white disabled:opacity-60">{busy ? "Processing…" : "Confirm Purchase"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function TopUpModal({ portfolio, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [funding, setFunding] = useState("direct");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!(parseFloat(amount) > 0)) { toast.error("Enter a valid amount."); return; }
    setBusy(true);
    try {
      await api.post(`/portfolios/${portfolio.id}/topup`, { amount: parseFloat(amount), funding_source: funding });
      toast.success("Top-up applied.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Top-up failed.");
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Top Up — ${portfolio.portfolio_type}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Amount (USD)</label>
          <input data-testid="topup-amount" type="number" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label className="block text-sm text-[#cbd5e1] mb-1.5">Funding Source</label>
          <select data-testid="topup-funding" className={inputCls} value={funding} onChange={(e) => setFunding(e.target.value)}>
            <option value="direct" className="bg-[#08090F]">Direct Deposit</option>
            <option value="main" className="bg-[#08090F]">Main Wallet</option>
            <option value="staking" className="bg-[#08090F]">Staking Wallet</option>
            <option value="referral" className="bg-[#08090F]">Referral Wallet</option>
          </select>
        </div>
        <button onClick={submit} disabled={busy} data-testid="topup-confirm" className="w-full px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white disabled:opacity-60">{busy ? "Processing…" : "Confirm Top-Up"}</button>
      </div>
    </Modal>
  );
}

function HistoryModal({ portfolio, onClose }) {
  const [hist, setHist] = useState(null);
  useEffect(() => { api.get(`/portfolios/${portfolio.id}/history`).then((r) => setHist(r.data)).catch(() => {}); }, [portfolio.id]);
  return (
    <Modal title="Portfolio History" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold text-white mb-2">Payouts</p>
          <div className="rounded-lg border border-white/10 max-h-52 overflow-y-auto divide-y divide-white/5">
            {hist?.payouts?.length ? hist.payouts.map((x) => (
              <div key={x.id} className="flex justify-between px-3 py-2 text-xs">
                <span className="text-[#94A3B8]">Day {x.day_index}</span>
                <span className="font-mono text-[#cbd5e1]">M {fmtCents(x.main_cents)} · S {fmtCents(x.staking_cents)}</span>
              </div>
            )) : <p className="px-3 py-4 text-xs text-[#64748B] font-mono">No payouts yet.</p>}
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold text-white mb-2">Top-ups</p>
          <div className="rounded-lg border border-white/10 max-h-40 overflow-y-auto divide-y divide-white/5">
            {hist?.topups?.length ? hist.topups.map((x) => (
              <div key={x.id} className="flex justify-between px-3 py-2 text-xs">
                <span className="text-[#94A3B8]">{fmtDate(x.created_at)} · {x.funding_source}</span>
                <span className="font-mono text-[#cbd5e1]">{fmtCents(x.amount_cents)}</span>
              </div>
            )) : <p className="px-3 py-4 text-xs text-[#64748B] font-mono">No top-ups.</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function QuantumVerse() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "make";
  const [configs, setConfigs] = useState([]);
  const [active, setActive] = useState([]);
  const [expired, setExpired] = useState([]);
  const [buying, setBuying] = useState(null);
  const [topping, setTopping] = useState(null);
  const [history, setHistory] = useState(null);

  const load = () => {
    api.get("/portfolio-configs").then((r) => setConfigs(r.data)).catch(() => {});
    api.get("/portfolios?status=active").then((r) => setActive(r.data)).catch(() => {});
    api.get("/portfolios?status=expired").then((r) => setExpired(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-white/10">
        {[["make", "Make Purchase"], ["active", "Active Purchases"], ["expired", "Expired Purchases"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`qv-tab-${k}`} className={`px-4 py-3 text-sm border-b-2 -mb-px ${tab === k ? "border-[#2DD4BF] text-white" : "border-transparent text-[#94A3B8] hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "make" && (
        <div className="grid md:grid-cols-3 gap-5" data-testid="qv-configs">
          {configs.map((c) => (
            <div key={c.id} className="rounded-xl border border-white/10 bg-[#0A0B10] p-6 flex flex-col">
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-[#2DD4BF]/10 text-[#2DD4BF] mb-4"><Planet size={22} weight="duotone" /></span>
              <h3 className="text-lg font-bold text-white">{c.name}</h3>
              <p className="text-sm text-[#94A3B8] mt-1.5 flex-1">{c.description}</p>
              <div className="mt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-[#64748B]">Range</span><span className="text-white font-mono">{fmtCents(c.min_cents)}–{fmtCents(c.max_cents)}</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Term</span><span className="text-white">12 months</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Daily upside cap</span><span className="text-[#2DD4BF] font-mono">Up to {(c.daily_rate_bps / 100).toFixed(2)}%</span></div>
              </div>
              <p className="mt-3 rounded-lg border border-amber-400/20 bg-amber-400/[0.05] p-3 text-[11px] leading-relaxed text-amber-300/90" data-testid={`qv-honest-${c.order}`}>
                Up to <span className="font-semibold">{(c.daily_rate_bps / 100).toFixed(2)}%</span> daily <span className="font-semibold">(capped)</span> — based on real Q-Core performance. Returns vary day to day and <span className="text-red-300 font-semibold">can be negative</span>. Losses are <span className="text-red-300 font-semibold">not capped</span>. Not guaranteed.
              </p>
              <button onClick={() => setBuying(c)} data-testid={`qv-buy-${c.order}`} className="mt-4 w-full px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white transition-colors">Select Portfolio</button>
            </div>
          ))}
          <div className="md:col-span-3">
            <PerformanceTerms />
          </div>
        </div>
      )}

      {tab === "active" && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="qv-active">
          {active.length ? active.map((p) => (
            <div key={p.id} className="rounded-xl border border-white/10 bg-[#0A0B10] p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-white">{p.portfolio_type}</h3>
                  <p className="text-xs text-[#64748B] font-mono">{p.id.slice(-8).toUpperCase()}</p>
                </div>
                <StatusPill status={p.status === "ACTIVE" ? "ACTIVE" : "monitoring"} />
              </div>
              <div className="grid grid-cols-2 gap-2.5 text-sm">
                <div><p className="text-[#64748B] text-xs">Current value</p><p className="font-mono text-white">{fmtCents(p.current_value_cents)}</p></div>
                <div><p className="text-[#64748B] text-xs">Total P&amp;L</p><p className={`font-mono ${p.total_earned_cents < 0 ? "text-red-400" : "text-emerald-300"}`}>{fmtCents(p.total_earned_cents)}</p></div>
                <div><p className="text-[#64748B] text-xs">Payout</p><p className="text-white capitalize">{p.payout_type}</p></div>
                <div><p className="text-[#64748B] text-xs">Allocation</p><p className="text-white text-xs">{p.current_allocation}</p></div>
                <div><p className="text-[#64748B] text-xs">Max daily (cap)</p><p className="font-mono text-[#2DD4BF]">{fmtCents(p.expected_daily_cents)}</p></div>
                <div><p className="text-[#64748B] text-xs">Days remaining</p><p className="font-mono text-white">{p.days_remaining}</p></div>
              </div>
              <div className="mt-3">
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#0E9E90] to-[#2DD4BF]" style={{ width: `${(p.day_index / 360) * 100}%` }} /></div>
                <p className="text-[10px] text-[#64748B] font-mono mt-1">Day {p.day_index} / 360 · {fmtDate(p.start_date)} → {fmtDate(p.end_date)}</p>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setTopping(p)} data-testid={`qv-topup-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white"><Plus size={14} weight="bold" /> Top Up</button>
                <button onClick={() => setHistory(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white text-sm hover:bg-white/5"><ClockCounterClockwise size={14} /> History</button>
              </div>
            </div>
          )) : <p className="text-sm text-[#64748B] font-mono">No active portfolios. Create one in "Make Purchase".</p>}
        </div>
      )}

      {tab === "expired" && (
        <div className="rounded-xl border border-white/10 bg-[#0A0B10] overflow-hidden" data-testid="qv-expired">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#64748B] border-b border-white/10">
                <th className="px-5 py-3">Portfolio</th><th className="px-5 py-3">Original</th><th className="px-5 py-3">Top-ups</th><th className="px-5 py-3">Earned</th><th className="px-5 py-3">Ended</th><th className="px-5 py-3">Status</th>
              </tr></thead>
              <tbody>
                {expired.length ? expired.map((p) => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="px-5 py-3.5 text-white">{p.portfolio_type}</td>
                    <td className="px-5 py-3.5 font-mono text-[#cbd5e1]">{fmtCents(p.initial_cents)}</td>
                    <td className="px-5 py-3.5 font-mono text-[#cbd5e1]">{fmtCents(p.topups_cents)}</td>
                    <td className={`px-5 py-3.5 font-mono ${p.total_earned_cents < 0 ? "text-red-400" : "text-emerald-300"}`}>{fmtCents(p.total_earned_cents)}</td>
                    <td className="px-5 py-3.5 text-[#94A3B8]">{fmtDate(p.end_date)}</td>
                    <td className="px-5 py-3.5"><span className="font-mono text-[10px] text-[#64748B]">{p.status}</span></td>
                  </tr>
                )) : <tr><td colSpan={6} className="px-5 py-6 text-sm text-[#64748B] font-mono">No expired portfolios.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {buying && <PurchaseFlow config={buying} onClose={() => setBuying(null)} onDone={() => { setBuying(null); load(); setTab("active"); }} />}
      {topping && <TopUpModal portfolio={topping} onClose={() => setTopping(null)} onDone={() => { setTopping(null); load(); }} />}
      {history && <HistoryModal portfolio={history} onClose={() => setHistory(null)} />}
    </div>
  );
}
